/**
 * Back-office UI languages — the agreed set (2026-10-02), picked from the
 * currencies we serve: th, en, zh-TW (TWD, TC1), vi (VND), es (UYU, VES),
 * fr (XOF, XAF, TND), tr (TRY), uk (UAH), ru (UZS, TMT).
 *
 * Thai is the default and the source language: every key is written in
 * src/messages/th.json first. English must always be complete — it is what the
 * other languages fall back to for a key they have not translated yet
 * (`pnpm i18n:check` warns about those, it does not fail).
 *
 * Adding a language: write src/messages/<code>.json, add the code below with
 * its label and Intl tag, add its loader in I18nProvider (messages + antd
 * locale + dayjs locale), and check the font stack in globals.css covers its
 * script.
 */
export const LOCALES = [
	"th",
	"en",
	"zh-TW",
	"vi",
	"es",
	"fr",
	"tr",
	"uk",
	"ru",
] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "th";

/** localStorage key for the viewer's choice; the pre-paint script reads it too. */
export const LOCALE_STORAGE_KEY = "locale";

/** Picker labels: each language written in itself, no flags (one language spans many countries). */
export const LOCALE_LABELS: Record<Locale, string> = {
	th: "ไทย",
	en: "English",
	"zh-TW": "繁體中文",
	vi: "Tiếng Việt",
	es: "Español",
	fr: "Français",
	tr: "Türkçe",
	uk: "Українська",
	ru: "Русский",
};

/**
 * BCP 47 tag for Intl / toLocale*String. th-TH keeps the Buddhist-era year the
 * Thai screens have always shown; es-419 is Latin American Spanish.
 */
export const INTL_LOCALES: Record<Locale, string> = {
	th: "th-TH",
	en: "en-US",
	"zh-TW": "zh-TW",
	vi: "vi-VN",
	es: "es-419",
	fr: "fr-FR",
	tr: "tr-TR",
	uk: "uk-UA",
	ru: "ru-RU",
};

export const isLocale = (value: unknown): value is Locale =>
	typeof value === "string" && (LOCALES as readonly string[]).includes(value);
